import { useEffect, useState, useRef } from 'react'
import { usePermission } from '../../hooks/usePermission'
import {
  getSupportTickets,
  getSupportTicket,
  assignSupportTicket,
  updateSupportTicket,
  replySupportTicket,
  type SupportTicketSummary,
  type SupportTicketDetail,
} from '../../services/adminApi'

const STATUS_COLORS: Record<string, { bg: string; text: string; darkBg: string; darkText: string }> = {
  open: { bg: 'bg-yellow-100', text: 'text-yellow-700', darkBg: 'dark:bg-yellow-900/30', darkText: 'dark:text-yellow-400' },
  in_progress: { bg: 'bg-blue-100', text: 'text-blue-700', darkBg: 'dark:bg-blue-900/30', darkText: 'dark:text-blue-400' },
  resolved: { bg: 'bg-green-100', text: 'text-green-700', darkBg: 'dark:bg-green-900/30', darkText: 'dark:text-green-400' },
  closed: { bg: 'bg-gray-100', text: 'text-gray-600', darkBg: 'dark:bg-gray-800', darkText: 'dark:text-gray-400' },
}

const TYPE_LABELS: Record<string, string> = { help: '❓ Help', bug: '🐛 Bug', feedback: '💬 Feedback' }

function StatusBadge({ status }: { status: string }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.open
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text} ${c.darkBg} ${c.darkText}`}>
      {status.replace('_', ' ')}
    </span>
  )
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

export function Support() {
  const canRead = usePermission('support_tickets', 'read')
  const canEdit = usePermission('support_tickets', 'edit')

  const [tickets, setTickets] = useState<SupportTicketSummary[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')

  const [selectedTicket, setSelectedTicket] = useState<SupportTicketDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [replyText, setReplyText] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [saving, setSaving] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const fetchTickets = async () => {
    setLoading(true)
    const res = await getSupportTickets({ status: statusFilter || undefined, type: typeFilter || undefined, page, page_size: 20 })
    if (res.ok && res.data) { setTickets(res.data.tickets); setTotal(res.data.total) }
    setLoading(false)
  }

  useEffect(() => { if (canRead) fetchTickets() }, [canRead, page, statusFilter, typeFilter])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [selectedTicket?.messages])

  const openTicket = async (id: string) => {
    setDetailLoading(true); setSelectedTicket(null); setReplyText(''); setIsInternal(false)
    const res = await getSupportTicket(id)
    if (res.ok && res.data) setSelectedTicket(res.data)
    setDetailLoading(false)
  }

  const handleAssign = async () => {
    if (!selectedTicket) return; setSaving(true)
    const res = await assignSupportTicket(selectedTicket.id)
    if (res.ok && res.data) { setSelectedTicket(res.data); fetchTickets() }
    setSaving(false)
  }

  const handleUpdateStatus = async (status: string) => {
    if (!selectedTicket) return; setSaving(true)
    const res = await updateSupportTicket(selectedTicket.id, { status })
    if (res.ok && res.data) { setSelectedTicket(res.data); fetchTickets() }
    setSaving(false)
  }

  const handleReply = async () => {
    if (!selectedTicket || !replyText.trim()) return; setSaving(true)
    const res = await replySupportTicket(selectedTicket.id, replyText.trim(), isInternal)
    if (res.ok && res.data) { setSelectedTicket(res.data); setReplyText(''); fetchTickets() }
    setSaving(false)
  }

  if (!canRead) return <div className="p-8 text-gray-500">No permission to view support tickets.</div>

  return (
    <div className="flex gap-6 h-[calc(100vh-120px)]">
      {/* Left: Ticket list */}
      <div className="w-[400px] flex flex-col shrink-0">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Support</h1>
          <span className="text-sm text-gray-400">{total}</span>
        </div>

        <div className="flex gap-2 mb-4">
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1a1625] text-xs px-2 py-1.5 text-gray-700 dark:text-gray-300">
            <option value="">All</option><option value="open">Open</option><option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option><option value="closed">Closed</option>
          </select>
          <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1) }}
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1a1625] text-xs px-2 py-1.5 text-gray-700 dark:text-gray-300">
            <option value="">All types</option><option value="help">Help</option><option value="bug">Bug</option><option value="feedback">Feedback</option>
          </select>
        </div>

        <div className="flex-1 overflow-y-auto space-y-1">
          {loading ? (
            <div className="flex justify-center py-12"><div className="h-6 w-6 animate-spin rounded-full border-2 border-[#7f4f9a] border-t-transparent" /></div>
          ) : tickets.length === 0 ? (
            <p className="text-center py-12 text-gray-400 text-sm">No tickets</p>
          ) : tickets.map((t) => (
            <div key={t.id} onClick={() => openTicket(t.id)}
              className={`p-3 rounded-xl cursor-pointer transition-colors border ${
                selectedTicket?.id === t.id
                  ? 'border-[#7f4f9a] bg-[#7f4f9a]/5 dark:bg-[#7f4f9a]/10'
                  : 'border-transparent hover:bg-gray-50 dark:hover:bg-[#1a1625]'
              }`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs">{TYPE_LABELS[t.type]}</span>
                <StatusBadge status={t.status} />
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-200 truncate">{t.subject}</p>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-gray-400 truncate">{t.user_email || t.user_id.slice(0, 8)}</span>
                <span className="text-xs text-gray-400">{formatDate(t.created_at)}</span>
              </div>
            </div>
          ))}
        </div>

        {total > 20 && (
          <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="text-xs px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40">Prev</button>
            <span className="text-xs text-gray-400">{page}/{Math.ceil(total / 20)}</span>
            <button onClick={() => setPage(page + 1)} disabled={page * 20 >= total} className="text-xs px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>

      {/* Right: Conversation */}
      <div className="flex-1 flex flex-col bg-white dark:bg-[#0f0d12] rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {!selectedTicket && !detailLoading ? (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
            Select a ticket to view
          </div>
        ) : detailLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#7f4f9a] border-t-transparent" />
          </div>
        ) : selectedTicket && (
          <>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm">{TYPE_LABELS[selectedTicket.type]}</span>
                  <StatusBadge status={selectedTicket.status} />
                </div>
                <div className="flex gap-2">
                  {canEdit && selectedTicket.status === 'open' && (
                    <button onClick={handleAssign} disabled={saving} className="px-3 py-1 text-xs rounded-lg bg-[#7f4f9a] text-white hover:bg-[#6b4282] disabled:opacity-40">Assign to Me</button>
                  )}
                  {canEdit && selectedTicket.status !== 'resolved' && selectedTicket.status !== 'closed' && (
                    <button onClick={() => handleUpdateStatus('resolved')} disabled={saving} className="px-3 py-1 text-xs rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-40">Resolve</button>
                  )}
                  {canEdit && selectedTicket.status !== 'closed' && (
                    <button onClick={() => handleUpdateStatus('closed')} disabled={saving} className="px-3 py-1 text-xs rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-40">Close</button>
                  )}
                </div>
              </div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{selectedTicket.subject}</h2>
              <div className="flex gap-4 mt-1 text-xs text-gray-400">
                <span>{selectedTicket.user_email || selectedTicket.user_id.slice(0, 8)}</span>
                <span>{selectedTicket.device_os || '—'}</span>
                <span>v{selectedTicket.app_version || '?'}</span>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {selectedTicket.messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                    msg.is_internal
                      ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700'
                      : msg.sender_type === 'admin'
                        ? 'bg-[#7f4f9a]/10 dark:bg-[#7f4f9a]/20'
                        : 'bg-gray-100 dark:bg-[#1a1625]'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium ${
                        msg.sender_type === 'admin' ? 'text-[#7f4f9a]' : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {msg.sender_type === 'admin' ? 'Support' : 'User'}
                      </span>
                      {msg.is_internal && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-200 dark:bg-amber-800 text-amber-700 dark:text-amber-300 font-medium">
                          Internal
                        </span>
                      )}
                      <span className="text-[10px] text-gray-400">{formatTime(msg.created_at)}</span>
                    </div>
                    <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{msg.body}</p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply input */}
            {canEdit && selectedTicket.status !== 'closed' && (
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3 mb-2">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)}
                      className="rounded border-gray-300 text-amber-500 focus:ring-amber-500" />
                    <span className={`text-xs ${isInternal ? 'text-amber-600 font-medium' : 'text-gray-500'}`}>
                      Internal note
                    </span>
                  </label>
                  {!isInternal && <span className="text-xs text-gray-400">💡 User will be notified</span>}
                </div>
                <div className="flex gap-2">
                  <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={2}
                    className="flex-1 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1a1625] text-sm p-3 text-gray-800 dark:text-gray-200 resize-none"
                    placeholder={isInternal ? 'Add internal note...' : 'Reply to user...'}
                  />
                  <button onClick={handleReply} disabled={saving || !replyText.trim()}
                    className={`self-end px-5 py-2 text-sm rounded-xl text-white disabled:opacity-40 ${
                      isInternal ? 'bg-amber-500 hover:bg-amber-600' : 'bg-[#7f4f9a] hover:bg-[#6b4282]'
                    }`}>
                    {saving ? '...' : isInternal ? 'Note' : 'Reply'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
